import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Download, Printer } from 'lucide-react';
import { useRegistrationConfirmation } from '../../hooks/useRegistrationConfirmation';
import { notifyFailure, notifyAPIClient } from '../../utils/toast';
import { useIsMobile } from '../../hooks/useResponsive';
import {
  ConfirmationLanguageCode,
  RegistrationConfirmation,
} from '../../types/RegistrationConfirmation.type';
import ConfirmationTemplateEditor from './ConfirmationTemplateEditor';
import RegistrationConfirmationDocument from '../Registration/RegistrationConfirmationDocument';

type RegistrationConfirmationDialogProps = {
  open: boolean;
  registrationId: number | null;
  mode: 'send' | 'download';
  handleClose: () => void;
  /** Called after the confirmation PDF is successfully downloaded. */
  onDownloaded?: (registrationId: number) => void;
};

const waitForDocumentImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));

  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          const resolveImage = () => {
            image.removeEventListener('load', resolveImage);
            image.removeEventListener('error', resolveImage);
            resolve();
          };

          image.addEventListener('load', resolveImage, { once: true });
          image.addEventListener('error', resolveImage, { once: true });
        }),
    ),
  );
};

const toDateInputValue = (value: string) => {
  const trimmedValue = value.trim();
  const dayFirstMatch = trimmedValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/,
  );

  if (dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const isoMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year}-${month}-${day}`;
  }

  return '';
};

const fromDateInputValue = (value: string) => {
  const [year, month, day] = value.split('-');

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const RegistrationConfirmationDialog: React.FC<
  RegistrationConfirmationDialogProps
> = ({ open, registrationId, mode, handleClose, onDownloaded }) => {
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState<RegistrationConfirmation | null>(null);
  const [previewLanguage, setPreviewLanguage] =
    useState<ConfirmationLanguageCode>('vi');
  const [downloading, setDownloading] = useState(false);
  const documentRef = useRef<HTMLDivElement>(null);
  const { confirmation, loading, error } = useRegistrationConfirmation(
    registrationId,
    open,
  );

  useEffect(() => {
    if (confirmation) {
      setDraft(confirmation);
      setPreviewLanguage('vi');
    } else if (!open) {
      setDraft(null);
    }
  }, [confirmation, open]);

  const handlePrint = () => {
    if (!documentRef.current) {
      notifyFailure();
      return;
    }

    const cleanup = () => {
      document.body.classList.remove('registration-confirmation-printing');
      window.removeEventListener('afterprint', cleanup);
    };

    document.body.classList.add('registration-confirmation-printing');
    window.addEventListener('afterprint', cleanup);
    void Promise.all([
      document.fonts.ready,
      waitForDocumentImages(documentRef.current),
    ]).then(() => window.print());
  };

  const handleDownload = async () => {
    if (!documentRef.current || !draft) {
      notifyFailure();
      return;
    }

    setDownloading(true);
    try {
      const { default: html2pdf } = await import('html2pdf.js');
      await document.fonts.ready;
      await waitForDocumentImages(documentRef.current);
      documentRef.current.classList.add('registration-confirmation-pdf-export');
      // Filename: "<customer fullname>_Confirmation_<language>_<registration num>".
      const safeFullName =
        draft.customer.fullName
          ?.trim()
          .replace(/[\\/:*?"<>|]+/g, '')
          .replace(/\s+/g, ' ')
          .trim() || 'Customer';
      const options = {
        margin: 0,
        filename: `${safeFullName}_Confirmation_${previewLanguage}_${draft.registrationId}.pdf`,
        image: { type: 'png' as const },
        html2canvas: {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
          imageTimeout: 15000,
          letterRendering: true,
        },
        pagebreak: {
          mode: ['css'] as const,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait' as const,
        },
      };

      await html2pdf().set(options).from(documentRef.current).save();
      notifyAPIClient('Registration confirmation downloaded successfully.');
      // Let the caller mark this registration as confirmation-downloaded.
      if (registrationId != null) onDownloaded?.(registrationId);
    } catch (downloadError) {
      console.error(
        'Failed to download registration confirmation:',
        downloadError,
      );
      notifyFailure();
    } finally {
      documentRef.current?.classList.remove(
        'registration-confirmation-pdf-export',
      );
      setDownloading(false);
    }
  };

  const closeDialog = () => {
    document.body.classList.remove('registration-confirmation-printing');
    setDraft(null);
    handleClose();
  };

  const updateCustomerField = (
    field: 'fullName' | 'memberCode' | 'phone',
    value: string,
  ) => {
    setDraft((currentDraft) =>
      currentDraft
        ? {
            ...currentDraft,
            customer: {
              ...currentDraft.customer,
              [field]: value,
            },
          }
        : currentDraft,
    );
  };

  const updateProductField = (
    field: 'displayName' | 'activationDate' | 'expiryDate',
    value: string,
  ) => {
    setDraft((currentDraft) =>
      currentDraft
        ? {
            ...currentDraft,
            product: {
              ...currentDraft.product,
              [field]: value,
            },
          }
        : currentDraft,
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      scroll="paper"
      open={open}
      onClose={closeDialog}
    >
      <DialogTitle>
        {mode === 'send'
          ? 'Send registration confirmation'
          : 'Registration confirmation'}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && draft && (
          <Stack spacing={3}>
            {mode === 'send' && (
              <Alert severity="info">
                No delivery channel is configured for this registration
                confirmation. Nothing has been sent. Use Download PDF or Print
                to prepare the document.
              </Alert>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customer information is pre-filled from the Registration and can
                be adjusted for this confirmation only.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label="Full name"
                  value={draft.customer.fullName}
                  onChange={(event) =>
                    updateCustomerField('fullName', event.target.value)
                  }
                  size="small"
                />
                <TextField
                  label="Member code"
                  value={draft.customer.memberCode}
                  onChange={(event) =>
                    updateCustomerField('memberCode', event.target.value)
                  }
                  size="small"
                />
                <TextField
                  label="Phone"
                  value={draft.customer.phone || ''}
                  onChange={(event) =>
                    updateCustomerField('phone', event.target.value)
                  }
                  size="small"
                />
                <TextField
                  label="Service/package"
                  value={draft.product.displayName || draft.product.packageName}
                  onChange={(event) =>
                    updateProductField('displayName', event.target.value)
                  }
                  size="small"
                />
                <TextField
                  label="Activation date"
                  type="date"
                  value={toDateInputValue(draft.product.activationDate)}
                  onChange={(event) =>
                    updateProductField(
                      'activationDate',
                      fromDateInputValue(event.target.value),
                    )
                  }
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  label="Expiry date"
                  type="date"
                  value={toDateInputValue(draft.product.expiryDate)}
                  onChange={(event) =>
                    updateProductField(
                      'expiryDate',
                      fromDateInputValue(event.target.value),
                    )
                  }
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Box>
            </Box>

            <Divider />

            <ConfirmationTemplateEditor
              value={draft.template}
              onChange={(template) => setDraft({ ...draft, template })}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                overflowX: 'auto',
                pb: 1,
                width: '100%',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2, width: '100%', maxWidth: '210mm' }}
              >
                <Typography variant="h6">Preview</Typography>
                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  size="small"
                  value={previewLanguage}
                  onChange={(
                    _,
                    nextLanguage: ConfirmationLanguageCode | null,
                  ) => {
                    if (nextLanguage) setPreviewLanguage(nextLanguage);
                  }}
                  aria-label="Preview language"
                >
                  <ToggleButton value="vi" aria-label="Vietnamese preview">
                    Vietnamese
                  </ToggleButton>
                  <ToggleButton value="en" aria-label="English preview">
                    English
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <RegistrationConfirmationDocument
                ref={documentRef}
                confirmation={draft}
                language={previewLanguage}
              />
            </Box>
          </Stack>
        )}

        {!loading && !error && !draft && (
          <Alert severity="warning">
            Registration confirmation data is unavailable.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<Printer size={18} />}
          onClick={handlePrint}
          disabled={!draft || loading || Boolean(error)}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<Download size={18} />}
          onClick={handleDownload}
          disabled={!draft || loading || Boolean(error) || downloading}
        >
          {downloading ? 'Downloading...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationConfirmationDialog;
